import type { JSX, ReactNode } from 'react';
import { Link } from 'react-router';

import { ChevronRight, FolderKanban } from 'lucide-react';
import { Collapsible } from 'radix-ui';

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';

import type { SubProjectNavigationItem } from '../model';

interface Props {
  subProjects: SubProjectNavigationItem[];
  projectBasePath: string;
  pathname: string;
  searchQuery: string;
}

interface HighlightedProjectNameProps {
  name: string;
  normalizedSearchQuery: string;
}

const HighlightedProjectName = ({ name, normalizedSearchQuery }: HighlightedProjectNameProps): ReactNode => {
  if (!normalizedSearchQuery) return name;

  const lowerName = name.toLowerCase();
  const matchIndex = lowerName.indexOf(normalizedSearchQuery);
  if (matchIndex === -1) return name;

  const matchEnd = matchIndex + normalizedSearchQuery.length;

  return (
    <>
      {name.slice(0, matchIndex)}
      <span className="rounded bg-white/15 px-0.5 italic text-white">{name.slice(matchIndex, matchEnd)}</span>
      {name.slice(matchEnd)}
    </>
  );
};

const SubProjectsMenu = ({ subProjects, projectBasePath, pathname, searchQuery }: Props): JSX.Element => {
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  return (
    <SidebarMenu className="pr-1">
      {subProjects.map((project) => {
        const configPath = `${projectBasePath}/config/${project.id}`;
        const promptPath = `${projectBasePath}/prompt/${project.id}`;
        const multimediaPath = `${projectBasePath}/multimedia/${project.id}`;
        const propiedadesPath = `${projectBasePath}/propiedades/${project.id}`;
        const isProjectRoute = [configPath, promptPath, multimediaPath, propiedadesPath].includes(pathname);

        return (
          <Collapsible.Root key={project.id} asChild defaultOpen={isProjectRoute} className="group/project">
            <SidebarMenuItem>
              <Collapsible.Trigger asChild>
                <SidebarMenuButton isActive={isProjectRoute} tooltip={project.name} title={project.name}>
                  <FolderKanban />
                  <span className="min-w-0 flex-1 truncate">
                    <HighlightedProjectName name={project.name} normalizedSearchQuery={normalizedSearchQuery} />
                  </span>
                  <ChevronRight className="ml-auto shrink-0 transition-transform group-data-[state=open]/project:rotate-90" />
                </SidebarMenuButton>
              </Collapsible.Trigger>
              <Collapsible.Content>
                <SidebarMenuSub className="mx-2 px-2 py-1">
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton asChild isActive={pathname === configPath}>
                      <Link to={configPath}>
                        <span>Configuración</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton asChild isActive={pathname === promptPath}>
                      <Link to={promptPath}>
                        <span>Prompt</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton asChild isActive={pathname === multimediaPath}>
                      <Link to={multimediaPath}>
                        <span>Multimedia</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton asChild isActive={pathname === propiedadesPath}>
                      <Link to={propiedadesPath}>
                        <span>Propiedades</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </Collapsible.Content>
            </SidebarMenuItem>
          </Collapsible.Root>
        );
      })}
    </SidebarMenu>
  );
};

export default SubProjectsMenu;
